import React, { useState } from "react";
import { Box, Link, Typography, TextField } from "@mui/material";
import ReferringProviderModal from "../modals/ReferringProviderModal";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { ReferringProviderSnapshot } from "./types";
import { useFlash } from "../FlashContext";

interface ReferringProviderProps {
  renderingProviders: any[];
  claim: {
    id: number;
    referring_provider_snapshot_attributes?: ReferringProviderSnapshot;
  };
  onProviderRemoved?: () => void;
  handleProviderSaved?: (providerId: number) => void;
  claimStatus: string;
  isRequired?: boolean;
  providerRole?: string;
}

const ReferringProvider: React.FC<ReferringProviderProps> = ({
  renderingProviders,
  claim,
  onProviderRemoved,
  handleProviderSaved,
  claimStatus,
  isRequired = false,
  providerRole = "referring",
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const hasReferringProvider =
    claim.referring_provider_snapshot_attributes !== undefined;

  const getProviderTypeHeading = (type: string) => {
    switch (type) {
      case "referring":
        return "Referring Provider";
      case "supervising":
        return "Supervising Provider";
      case "ordering":
        return "Ordering Provider";
      default:
        return "Provider";
    }
  };

  const handleRemoveProvider = () => {
    fetch(
      `/referring_provider_snapshots/${claim.referring_provider_snapshot_attributes.id}`,
      {
        method: "DELETE",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to remove additional provider");
        }
        return response.json();
      })
      .then((data) => {
        showToast({
          title: "Provider Removed",
          text: "Additional provider removed successfully",
          type: "success",
        });
        if (onProviderRemoved) {
          onProviderRemoved();
        }
      })
      .catch((error) => {
        console.error("Error removing additional provider:", error);
        showToast({
          title: "Failed to Remove Provider",
          text: "Failed to remove additional provider",
          type: "error",
        });
      });
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        padding: "20px",
        height: "250px",
      }}
    >
      <Typography
        sx={{
          color: "#707070",
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: hasReferringProvider ? "15px" : "26px",
        }}
      >
        {hasReferringProvider
          ? getProviderTypeHeading(
              claim.referring_provider_snapshot_attributes.provider_qual
            )
          : getProviderTypeHeading(providerRole)}
      </Typography>
      {hasReferringProvider ? (
        <Box sx={{ mt: 0 }}>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#707070",
              mb: 0,
            }}
          >
            {claim.referring_provider_snapshot_attributes.is_in_organization
              ? "In-house Provider"
              : "Outside Provider"}
          </Typography>

          <Typography
            sx={{
              color: "#A29D9B",
              fontWeight: 700,
              fontSize: "12px",
              lineHeight: "26px",
            }}
          >
            Provider
          </Typography>
          <Box className="field-container" sx={{ marginLeft: "0px", mb: 0 }}>
            <TextField
              fullWidth
              value={`${
                claim.referring_provider_snapshot_attributes.first_name
              } ${
                claim.referring_provider_snapshot_attributes.middle_name || ""
              } ${claim.referring_provider_snapshot_attributes.last_name}`}
              variant="outlined"
              autoComplete="off"
              size="small"
              InputProps={{
                readOnly: true,
                sx: {
                  "& input": {
                    padding: "10px !important",
                  },
                },
              }}
              sx={{
                width: "100% !important",
              }}
            />
          </Box>

          <Typography
            sx={{
              color: "#A29D9B",
              fontWeight: 700,
              fontSize: "12px",
              lineHeight: "26px",
            }}
          >
            Provider NPI
          </Typography>
          <Box className="field-container" sx={{ marginLeft: "0px", mb: 0 }}>
            <TextField
              fullWidth
              value={claim.referring_provider_snapshot_attributes.npi}
              variant="outlined"
              size="small"
              autoComplete="off"
              InputProps={{
                readOnly: true,
                sx: {
                  "& input": {
                    padding: "10px !important",
                  },
                },
              }}
              sx={{
                width: "100% !important",
              }}
            />
          </Box>

          <Typography
            sx={{
              color: "#A29D9B",
              fontWeight: 700,
              fontSize: "12px",
              lineHeight: "26px",
            }}
          >
            Provider Taxonomy
          </Typography>
          <Box className="field-container" sx={{ marginLeft: "0px", mb: 0 }}>
            <TextField
              fullWidth
              value={claim.referring_provider_snapshot_attributes.provider_tax}
              variant="outlined"
              size="small"
              autoComplete="off"
              InputProps={{
                readOnly: true,
                sx: {
                  "& input": {
                    padding: "10px !important",
                  },
                },
              }}
              sx={{
                width: "100% !important",
              }}
            />
          </Box>

          <Link
            onClick={handleRemoveProvider}
            sx={{
              fontSize: "10px",
              lineHeight: "14px",
              textDecoration: "underline",
              color: "#3F51B5",
              fontWeight: 400,
              cursor: "pointer",
              display: "block",
              mt: "5px",
            }}
          >
            I need to remove this provider
          </Link>
        </Box>
      ) : (
        <>
          <p
            style={{
              color: "#707070",
              fontSize: "12px",
              lineHeight: "16px",
              marginBlockStart: "0px",
              marginBlockEnd: "0px",
              marginBottom: "15px",
              marginTop: "5px",
              fontWeight: 400,
            }}
          >
            {isRequired
              ? `This claim does not currently have an additional provider listed. Including an additional provider can help ensure accurate claim submission and reduce the risk of delays or denials. You are required to add an additional provider to this claim.`
              : `This claim does not currently have a additional provider listed. Including a additional provider can help ensure accurate claim submission and reduce the risk of delays or denials. Would you like to add a additional provider to this claim now?`}
          </p>

          {claimStatus !== "submitted" &&
            claimStatus !== "pending_submission" && (
              <Link
                onClick={() => setModalOpen(true)}
                sx={{
                  fontSize: "10px",
                  lineHeight: "14px",
                  textDecoration: "underline",
                  color: "#3F51B5",
                  fontWeight: 400,
                  cursor: "pointer",
                }}
              >
                I need to add an additional provider
              </Link>
            )}
        </>
      )}

      <ReferringProviderModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        renderingProviders={renderingProviders}
        claimId={claim.id}
        onProviderSaved={handleProviderSaved}
        isRequired={isRequired}
        providerRole={providerRole}
      />
    </Box>
  );
};

export default ReferringProvider;
